import {
  enforceConsent,
  installUsOptOutSignal,
  readConsent,
  resetConsentCache,
  resetUsOptOutSignal,
  subscribeConsent,
  type ConsentDecision,
  type ConsentState,
} from '@redbrickmedia/duplex.user-identity'
import { isDebugEnabled } from '../debug/isDebugEnabled'
import debug from '../console'

const logger = debug.withTag('utils:consent')

let pending: Promise<ConsentDecision> | null = null
let current: ConsentDecision | null = null
let stopPackageSubscription: (() => void) | null = null
const subscribers = new Set<(decision: ConsentDecision) => void>()

function publish(decision: ConsentDecision): void {
  if (current === decision) return

  current = decision
  logger.debug(
    `Consent resolved: ${decision.state} (policy: ${decision.policy}, evidence: ${decision.evidence.join(',') || 'none'})`
  )
  subscribers.forEach((subscriber) => {
    try {
      subscriber(decision)
    } catch (error) {
      logger.warn('Consent subscriber failed', error)
    }
  })
}

function unknownErrorDecision(): ConsentDecision {
  return {
    state: 'unknown',
    policy: 'unknown',
    policies: ['unknown'],
    reason: 'error',
    evidence: ['error'],
    mayResolveIdentity: false,
    mustEraseStoredIdentity: false,
    restrictions: {
      restrictDataProcessing: true,
      nonPersonalizedAds: true,
    },
    revisable: true,
  }
}

function start(country?: string): Promise<ConsentDecision> {
  const options = {
    country,
    debug: isDebugEnabled(),
  }

  if (!pending) {
    try {
      installUsOptOutSignal()
      stopPackageSubscription ??= subscribeConsent(publish, options)
      pending = readConsent(options)
        .then((decision) => {
          publish(decision)
          return decision
        })
        .catch((error) => {
          logger.warn('Consent read failed; preserving unknown state', error)
          const decision = unknownErrorDecision()
          enforceConsent(decision, isDebugEnabled())
          publish(decision)
          return decision
        })
    } catch (error) {
      logger.warn(
        'Consent initialization failed; preserving unknown state',
        error
      )
      const decision = unknownErrorDecision()
      enforceConsent(decision, isDebugEnabled())
      publish(decision)
      pending = Promise.resolve(decision)
    }
  }

  return pending
}

export function resolveConsent(country?: string): Promise<ConsentDecision> {
  return start(country)
}

export function subscribeToConsent(
  subscriber: (decision: ConsentDecision) => void,
  country?: string
): () => void {
  subscribers.add(subscriber)
  if (current) {
    try {
      subscriber(current)
    } catch (error) {
      logger.warn('Consent subscriber failed', error)
    }
  }
  void start(country)

  return () => subscribers.delete(subscriber)
}

export async function whenConsentSettled(): Promise<void> {
  await resolveConsent()
}

export function consentSnapshot(): ConsentDecision | null {
  return current
}

export function identityConsentState(decision: ConsentDecision): ConsentState {
  if (decision.mayResolveIdentity) return 'allow'
  if (decision.mustEraseStoredIdentity) return 'deny'
  return 'unknown'
}

export function resetConsentGateForTests(): void {
  stopPackageSubscription?.()
  stopPackageSubscription = null
  pending = null
  current = null
  subscribers.clear()
  resetConsentCache()
  resetUsOptOutSignal()
}

export type { ConsentDecision }
